// ─────────────────────────────────────────────────────────────────────────────
// Location Shipping Exclusions Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, and reducer for the
// locationShippingExclusions slice (WB-7570). Create/Delete update the flat
// `items` array optimistically on success -- the list stays small enough
// that a full reload isn't needed after every mutation.
// ─────────────────────────────────────────────────────────────────────────────

import { Action, createReducer, on } from '@ngrx/store';
import { ILocationShippingExclusion } from '../models/location-shipping-exclusion.models';
import * as locationShippingExclusionsActions from './location-shipping-exclusions.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface LocationShippingExclusionsState {
  items: ILocationShippingExclusion[];
  loading: boolean;
  creating: boolean;
  error: string | null;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialLocationShippingExclusionsState: LocationShippingExclusionsState = {
  items: [],
  loading: false,
  creating: false,
  error: null,
};

// ── Reducer ──────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialLocationShippingExclusionsState,

  on(locationShippingExclusionsActions.LoadLocationShippingExclusions, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(locationShippingExclusionsActions.LoadLocationShippingExclusionsSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    items: response.data ?? [],
  })),

  on(locationShippingExclusionsActions.LoadLocationShippingExclusionsError, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(locationShippingExclusionsActions.CreateLocationShippingExclusion, (state) => ({
    ...state,
    creating: true,
    error: null,
  })),

  on(locationShippingExclusionsActions.CreateLocationShippingExclusionSuccess, (state, { exclusion }) => ({
    ...state,
    creating: false,
    items: [exclusion, ...state.items],
  })),

  on(locationShippingExclusionsActions.CreateLocationShippingExclusionError, (state, { error }) => ({
    ...state,
    creating: false,
    error,
  })),

  on(locationShippingExclusionsActions.BulkCreateLocationShippingExclusions, (state) => ({
    ...state,
    creating: true,
    error: null,
  })),

  on(locationShippingExclusionsActions.BulkCreateLocationShippingExclusionsSuccess, (state, { created }) => ({
    ...state,
    creating: false,
    items: [...created, ...state.items],
  })),

  on(locationShippingExclusionsActions.BulkCreateLocationShippingExclusionsError, (state, { error }) => ({
    ...state,
    creating: false,
    error,
  })),

  on(locationShippingExclusionsActions.DeleteLocationShippingExclusionSuccess, (state, { locationId }) => ({
    ...state,
    items: state.items.filter((item) => item.location_id !== locationId),
  })),

  on(locationShippingExclusionsActions.DeleteLocationShippingExclusionError, (state, { error }) => ({
    ...state,
    error,
  })),

  on(locationShippingExclusionsActions.BulkDeleteLocationShippingExclusions, (state) => ({
    ...state,
    loading: true,
  })),

  on(locationShippingExclusionsActions.BulkDeleteLocationShippingExclusionsSuccess, (state, { deleted }) => {
    const removed = new Set(deleted);
    return {
      ...state,
      loading: false,
      items: state.items.filter((item) => !removed.has(item.location_id)),
    };
  }),

  on(locationShippingExclusionsActions.BulkDeleteLocationShippingExclusionsError, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

);

export function locationShippingExclusionsReducer(state: LocationShippingExclusionsState | undefined, action: Action) {
  return featureReducer(state, action);
}
